/**
 * Menu API: fetch menu for date, book selection.
 */
import { apiGet, apiPost } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import { environment } from '../../../shared/config/environment';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import { resolveImageUri } from '../../../shared/utils/resolveImageUri';
import type { MenuResponse, MenuBuilder as ApiMenuBuilder, MenuItem as ApiMenuItem } from './apiTypes';
import type { MenuBuilder, MenuItemData, BookedItem } from './types';

/** API may send `meal_type_*`; domain uses `food_type_*`. Empty/missing must not become `''`. */
function coalesceFoodTypeCategory(raw: unknown): string {
  if (raw != null && String(raw).trim() !== '') {
    return String(raw);
  }
  return 'MEAL';
}

/** Normalize menu item + builder API fields to card tag `meal` | `snack`. */
export function resolveFoodTypeCategoryTag(
  item?: { food_type_category?: unknown; meal_type_category?: unknown } | null,
  builder?: {
    food_type_category?: unknown;
    meal_type_category?: unknown;
    type_name?: unknown;
    meal_type_name?: unknown;
  } | null,
): 'meal' | 'snack' {
  const raw =
    item?.food_type_category ??
    item?.meal_type_category ??
    builder?.food_type_category ??
    builder?.meal_type_category ??
    builder?.type_name ??
    builder?.meal_type_name ??
    'meal';
  const normalized = String(raw).trim().toLowerCase();
  if (normalized === 'snack' || normalized === 'snacks') {
    return 'snack';
  }
  return 'meal';
}

function resolveMenuItemAttachment(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || !raw.trim()) {
    return undefined;
  }
  return resolveImageUri(raw) ?? raw;
}

/** Normalize GET payloads: API JSON may use `meal_type_*`; domain uses `food_type_*`. */
function sanitizeBuilderItems(builder: MenuBuilder & { meal_type_id?: number }): MenuBuilder {
  const builder_fid = Number(builder.food_type_id ?? builder.meal_type_id) || 0;
  const type_data = (builder.type_data ?? []).map((it: MenuItemData) => {
    const raw = it as MenuItemData & { meal_type_category?: string; meal_type_id?: number };
    return {
      ...it,
      food_type_category: coalesceFoodTypeCategory(raw.food_type_category ?? raw.meal_type_category) as MenuItemData['food_type_category'],
      food_type_id: Number(raw.food_type_id ?? raw.meal_type_id ?? builder_fid) || 0,
      menu_builder_id: Number(raw.menu_builder_id ?? builder_fid) || 0,
      attachment: resolveMenuItemAttachment(raw.attachment),
    };
  });
  return { ...builder, food_type_id: builder_fid, type_data };
}

function normalizeBuilder(api_builder: ApiMenuBuilder): MenuBuilder {
  const items = api_builder.items ?? [];
  const builder_id = api_builder.meal_type_id;
  const type_data: MenuItemData[] = items.map((it: ApiMenuItem) => ({
    id: it.id,
    quantity: it.quantity ?? 0,
    is_selected: it.is_selected ?? it.quantity > 0,
    food_type_category: coalesceFoodTypeCategory(it.meal_type_category),
    food_type_id: it.meal_type_id ?? builder_id,
    is_customizations_available: (it as any).is_customizations_available ?? 0,
    menu_builder_id: builder_id,
    selection_number: Number((it as any).selection_number ?? 0),
    scaled_carbs: Number((it as any).scaled_carbs ?? 0),
    protein: String(it.protein ?? 0),
    calories: String(it.calories ?? 0),
    fats: String(it.fats ?? 0),
    carbs: String(it.carbs ?? 0),
    customizations: Array.isArray((it as any).customizations) ? (it as any).customizations : [],
    slug: '',
    name: it.name ?? '',
    a_name: (it as any).a_name ?? (it as any).name_ar,
    description: (it as any).description,
    a_description: (it as any).a_description,
    attachment: resolveMenuItemAttachment(it.attachment),
    type_name: api_builder.meal_type_name,
  }));
  const selected_quantity = type_data.reduce((sum, it) => sum + (it.quantity || 0), 0);
  const first = type_data[0];
  const type_name = api_builder.meal_type_name ?? first?.food_type_category ?? 'MEAL';
  const type_limit = 0;
  return {
    food_type_id: builder_id,
    type_name,
    a_type_name: (api_builder as any).a_type_name,
    type_limit,
    selected_quantity,
    type_data,
  };
}

export interface FetchMenuResult {
  menu_builders: MenuBuilder[];
  available_dates: string[];
  is_double_box: boolean;
}

type GuestFoodCategoryTag = 'meal' | 'snack';

/** Map menu_items `type_name` to card tag; unknown/empty → meal (guest default). */
function resolveGuestTypeNameToFoodCategory(type_name: unknown): GuestFoodCategoryTag {
  const normalized = String(type_name ?? '').trim().toLowerCase();
  if (normalized === 'meal' || normalized === 'meals') {
    return 'meal';
  }
  if (normalized === 'snack' || normalized === 'snacks') {
    return 'snack';
  }
  return 'meal';
}

function extractMenuItemId(raw: unknown): number | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const row = raw as Record<string, unknown>;
  const id = Number(row.id ?? row.menu_item_id);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Build id → meal|snack lookup from GET menu_items (res2). */
function buildGuestMenuItemCategoryLookup(menu_items_response: unknown): Map<number, GuestFoodCategoryTag> {
  const lookup = new Map<number, GuestFoodCategoryTag>();
  const envelope = menu_items_response as { data?: Record<string, unknown> } | Record<string, unknown> | null | undefined;
  const data = (envelope && typeof envelope === 'object' && 'data' in envelope ? envelope.data : envelope) as
    | Record<string, unknown>
    | null
    | undefined;
  const categories = data?.menu_items ?? data?.menuItems ?? data?.menu_builders;
  if (!Array.isArray(categories)) {
    return lookup;
  }
  for (const category of categories) {
    if (!category || typeof category !== 'object') {
      continue;
    }
    const group = category as Record<string, unknown>;
    const tag = resolveGuestTypeNameToFoodCategory(group.type_name ?? group.meal_type_name);
    const items = group.type_data ?? group.items;
    if (!Array.isArray(items)) {
      continue;
    }
    for (const item of items) {
      const id = extractMenuItemId(item);
      if (id != null) {
        lookup.set(id, tag);
      }
    }
  }
  return lookup;
}

/** Guest display uses menu_subscriptions items; meal/snack tags come from menu_items type_name. */
function applyGuestMenuItemCategories(menu_builders: MenuBuilder[], lookup: Map<number, GuestFoodCategoryTag>): MenuBuilder[] {
  return menu_builders.map(builder => ({
    ...builder,
    type_data: (builder.type_data ?? []).map(item => {
      const id = Number(item.id);
      const tag = Number.isFinite(id) && id > 0 ? (lookup.get(id) ?? 'meal') : 'meal';
      return {
        ...item,
        food_type_category: tag,
      };
    }),
  }));
}

function parseMenuBuildersFromResponse(data: MenuResponse['data'] | undefined): MenuBuilder[] {
  const raw = data?.menu_builders ?? [];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((b: any) => {
    const base: MenuBuilder =
      b.type_data && Array.isArray(b.type_data) ? (b as MenuBuilder) : normalizeBuilder(b as ApiMenuBuilder);
    return sanitizeBuilderItems(base);
  });
}

export async function fetchMenu(customer_id: number, date: string, is_double_box: boolean): Promise<FetchMenuResult> {
  const date_ascii = ensureAsciiDateString(date);
  const res = await apiGet<MenuResponse>(endpoints.menus, {
    customer_id,
    date: date_ascii,
    is_double_box,
  });
  const data = res?.data;
  const raw = data?.menu_builders ?? [];
  const menu_builders = Array.isArray(raw)
    ? raw.map((b: any) => {
        const base: MenuBuilder =
          b.type_data && Array.isArray(b.type_data) ? (b as MenuBuilder) : normalizeBuilder(b as ApiMenuBuilder);
        return sanitizeBuilderItems(base);
      })
    : [];
  try {
    const { recordPerDayMenuFetchResult } =
      require('../../../exDev/probes/subscriptionMenuProbe/perDayGate') as typeof import('../../../exDev/probes/subscriptionMenuProbe/perDayGate');
    recordPerDayMenuFetchResult(date_ascii, menu_builders.length);
  } catch {
    // dev-only
  }
  return {
    menu_builders,
    available_dates: data?.available_dates ?? [],
    is_double_box: data?.is_double_box ?? is_double_box,
  };
}

/**
 * GET via `endpoints.menuSubscriptions` (legacy menu preview / non-standard paths).
 * Guest (`customer_id === 0`): also GET `menu_items` to tag each item meal vs snack from `type_name`.
 */
export async function fetchFakeMenu(
  customer_id: number,
  date: string,
  is_double_box: boolean,
): Promise<FetchMenuResult> {
  const date_ascii = ensureAsciiDateString(date);
  const query = {
    customer_id,
    date: date_ascii,
    is_double_box,
  };
  const res = await apiGet<MenuResponse>(endpoints.menuSubscriptions, query);
  let menu_items_response: unknown;
  if (customer_id === 0) {
    menu_items_response = await apiGet<MenuResponse>(endpoints.menuItems, query);
  }
  const data = res?.data;
  let menu_builders = parseMenuBuildersFromResponse(data);
  if (customer_id === 0 && menu_items_response != null) {
    const category_lookup = buildGuestMenuItemCategoryLookup(menu_items_response);
    menu_builders = applyGuestMenuItemCategories(menu_builders, category_lookup);
  }
  return {
    menu_builders,
    available_dates: data?.available_dates ?? [],
    is_double_box: data?.is_double_box ?? is_double_box,
  };
}

/** Guest explore (no auth): `menu_subscriptions` with `customer_id: 0` and `is_double_box: false`. */
export async function fetchGuestMenuPreview(date: string): Promise<FetchMenuResult> {
  const date_ascii = ensureAsciiDateString(date);
  const query = {
    customer_id: 0,
    date: date_ascii,
    is_double_box: false,
  };
  if (__DEV__) {
    const search = `?${new URLSearchParams(Object.fromEntries(Object.entries(query).map(([k, v]) => [k, String(v)])))}`;
    const base = environment.API_URL.endsWith('/') ? environment.API_URL : `${environment.API_URL}/`;
    const subscriptions_url = `${base}${endpoints.menuSubscriptions.replace(/^\//, '')}${search}`;
    const menu_items_url = `${base}${endpoints.menuItems.replace(/^\//, '')}${search}`;

  }
  const result = await fetchFakeMenu(0, date, false);
  if (__DEV__) {

  }
  return result;
}

export interface BookMenuParams {
  customer_id: number;
  customer_subscription_id: number;
  date: string;
  remove_box_index: number;
  menu_items: BookedItem[] | Array<{ menu_item_id: number; quantity: number; menu_builder_id: number }>;
  is_double_box: boolean;
}

export interface BookMenuResponse {
  code?: number;
  message?: string;
  success?: boolean;
}

function isFullBookedRow(item: unknown): item is BookedItem {
  return typeof item === 'object' && item != null && 'food_type_category' in item && typeof (item as BookedItem).food_type_category === 'string';
}

function sanitizeCustomizationOption(o: Record<string, unknown>): Record<string, unknown> {
  const oid = Number((o as { option_id?: unknown }).option_id ?? (o as { id?: unknown }).id ?? 0);
  const option_id = Number.isFinite(oid) ? oid : 0;
  return {
    ...o,
    option_id,
    id: option_id,
    is_selected: Boolean((o as { is_selected?: unknown }).is_selected),
  };
}

function sanitizeCustomizationGroup(c: Record<string, unknown>): Record<string, unknown> {
  const raw = Array.isArray((c as { options?: unknown }).options) ? ((c as { options: unknown[] }).options as unknown[]) : [];
  const options = raw.map(entry =>
    sanitizeCustomizationOption(entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {}),
  );
  const cid = Number((c as { customization_id?: unknown }).customization_id ?? (c as { id?: unknown }).id ?? 0);
  const customization_id = Number.isFinite(cid) ? cid : 0;
  return {
    ...c,
    customization_id,
    id: customization_id,
    is_selected: (c as { is_selected?: unknown }).is_selected !== false,
    options,
  };
}

/**
 * Wire format for `select/menu_items` (POST): `food_type_*` keys and string `food_type_id`
 * match the backend contract (see product sample payload).
 */
function sanitizeBookedItemForApi(row: BookedItem): Record<string, unknown> {
  const food_type_category = String(row.food_type_category ?? 'meal').toLowerCase();
  const food_type_id = String(Number(row.food_type_id) || 0);
  const out: Record<string, unknown> = {
    food_type_category,
    food_type_id,
    menu_item_id: Number(row.menu_item_id) || 0,
    menu_builder_id: Number(row.menu_builder_id) || 0,
    selection_number: Number(row.selection_number) || 0,
    quantity: Number(row.quantity) || 0,
    scaled_carbs: Number(row.scaled_carbs) || 0,
  };
  const customizations = Array.isArray(row.customizations)
    ? row.customizations.map(c => sanitizeCustomizationGroup(c && typeof c === 'object' ? (c as Record<string, unknown>) : {}))
    : [];
  if (customizations.length > 0) {
    out.customizations = customizations;
  }
  return out;
}

/** Narrow updates (e.g. quantity-only) — same wire keys as full rows; unknown meal type sent as neutral defaults. */
function sanitizeMinimalMenuItem(item: { menu_item_id: number; quantity: number; menu_builder_id: number }): Record<string, unknown> {
  return {
    food_type_category: 'meal',
    food_type_id: '0',
    menu_item_id: Number(item.menu_item_id) || 0,
    menu_builder_id: Number(item.menu_builder_id) || 0,
    selection_number: 0,
    quantity: Number(item.quantity) || 0,
    scaled_carbs: 0,
  };
}

function sanitizeMenuItemsForApi(items: BookMenuParams['menu_items']): Record<string, unknown>[] {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map(raw => {
    if (isFullBookedRow(raw)) {
      return sanitizeBookedItemForApi(raw);
    }
    return sanitizeMinimalMenuItem(raw as { menu_item_id: number; quantity: number; menu_builder_id: number });
  });
}

export async function bookMenu(params: BookMenuParams): Promise<BookMenuResponse> {
  const date_ascii = ensureAsciiDateString(params.date);
  if (!date_ascii || date_ascii.length < 10) {
    return { success: false, code: 400, message: 'The date field is required.' };
  }
  const sub_id = Number(params.customer_subscription_id);
  if (!Number.isFinite(sub_id) || sub_id <= 0) {
    return { success: false, code: 400, message: 'customer_subscription_id is required.' };
  }
  const payload = {
    customer_id: params.customer_id,
    customer_subscription_id: sub_id,
    date: date_ascii,
    remove_box_index: Number(params.remove_box_index) || 0,
    menu_items: sanitizeMenuItemsForApi(params.menu_items),
    is_double_box: Boolean(params.is_double_box),
  };
  const res = await apiPost<BookMenuResponse>(endpoints.mealBooking, payload);
  return res ?? { code: 200, success: true };
}

export interface ClearMenuSelectionsParams {
  customer_id: number;
  date: string;
  is_double_box?: boolean;
}

export interface ClearMenuSelectionsResponse {
  code?: number;
  message?: string;
  success?: boolean;
}

export async function clearMenuSelections(params: ClearMenuSelectionsParams): Promise<ClearMenuSelectionsResponse> {
  const date_ascii = ensureAsciiDateString(params.date);
  const payload = {
    customer_id: params.customer_id,
    date: date_ascii,
    is_double_box: Boolean(params.is_double_box ?? false),
  };
  const res = await apiPost<ClearMenuSelectionsResponse>(endpoints.clearSelections, payload);
  return res ?? { code: 200, success: true };
}

export interface SendMenuRatingParams {
  rate: number;
  menu_item_id: number;
  date: string;
  customer_id: number;
  customer_subscription_id: number;
  comment?: string;
}

export interface SendMenuRatingResponse {
  code?: number;
  message?: string;
  success?: boolean;
}

export async function sendMenuRating(params: SendMenuRatingParams): Promise<SendMenuRatingResponse> {
  const date_ascii = ensureAsciiDateString(params.date);
  const payload = {
    rate: params.rate,
    menu_item_id: params.menu_item_id,
    date: date_ascii,
    customer_id: params.customer_id,
    customer_subscription_id: params.customer_subscription_id,
    comment: params.comment ?? '',
  };
  const res = await apiPost<SendMenuRatingResponse>(endpoints.sendRating, payload);
  return res ?? { code: 200, success: true };
}

export interface RemoveMenuItemFromBookingParams {
  customer_id: number;
  date: string;
  menu_item_id: number;
  menu_builder_id: number;
  customer_subscription_id?: number;
  is_double_box?: boolean;
  remove_box_index?: number;
}

/** Calendar popup delete — quantity 0 via `bookMenu` when subscription id is known. */
export async function removeMenuItemFromBooking(params: RemoveMenuItemFromBookingParams): Promise<BookMenuResponse> {
  const sub_id = Number(params.customer_subscription_id);
  if (Number.isFinite(sub_id) && sub_id > 0) {
    return bookMenu({
      customer_id: params.customer_id,
      customer_subscription_id: sub_id,
      date: params.date,
      remove_box_index: params.remove_box_index ?? 0,
      menu_items: [
        {
          menu_item_id: params.menu_item_id,
          quantity: 0,
          menu_builder_id: params.menu_builder_id,
        },
      ],
      is_double_box: params.is_double_box ?? false,
    });
  }
  const date_ascii = ensureAsciiDateString(params.date);
  const res = await apiPost<BookMenuResponse>(endpoints.mealBooking, {
    customer_id: params.customer_id,
    date: date_ascii,
    is_double_box: Boolean(params.is_double_box ?? false),
    menu_items: [
      {
        menu_item_id: params.menu_item_id,
        quantity: 0,
        menu_builder_id: params.menu_builder_id,
      },
    ],
  });
  return res ?? { code: 200, success: true };
}
